import { useMemo } from "react";

import { isBefore, isWithinInterval, startOfDay } from "date-fns";
import { useAtomValue } from "jotai";
import { EApprovalStatus, EBenefitContentTab, ETaxonomy, ICardBenefitPackage } from "library/types";
import { selectedClientAtom } from "store";
import {
  benefitContentTabAtom,
  selectedPackageUserAtom,
  showExpiredPackagesAtom,
} from "store/UIBenefitsPage";

import { useGetMsalUserEmail } from "hooks/useGetMsalUserEmail";
import { useTaxonomyOptions } from "hooks/useTaxonomyOptions";
import { useGetCompanyBenefitPackageViews } from "state/useGetCompanyBenefitPackageViews";
import { useGetEmployeeEnrollments } from "state/useGetEnrollments";

export function useBenefitsData() {
  const selectedClient = useAtomValue(selectedClientAtom);
  const userUniqueEmail = useGetMsalUserEmail();
  const selectedPackageUser = useAtomValue(selectedPackageUserAtom);
  const showExpired = useAtomValue(showExpiredPackagesAtom);
  const selectedBenefitContentTab = useAtomValue(benefitContentTabAtom);

  const showExpiredByTabSelection =
    selectedBenefitContentTab === EBenefitContentTab.all ||
    selectedBenefitContentTab === EBenefitContentTab.expired;

  const enrollmentStatusOptions = useTaxonomyOptions(ETaxonomy.ApprovalStatus);

  const { data: companyBenefitPackages } = useGetCompanyBenefitPackageViews({
    clientInternalNumberGos: Number(selectedClient?.clientInternalNumberGos),
    countryCode: Number(selectedClient?.countryCode),
    enabled: !!selectedClient,
  });

  const { data: employeeEnrollments } = useGetEmployeeEnrollments({
    clientInternalNumberGos: Number(selectedClient?.clientInternalNumberGos),
    countryCode: Number(selectedClient?.countryCode),
    enabled: !!selectedClient,
  });

  const packageData = useMemo(() => {
    const eligiblePackages = [] as ICardBenefitPackage[];
    const enrolledPackages = [] as ICardBenefitPackage[];
    if (!companyBenefitPackages || !employeeEnrollments)
      return {
        eligiblePackages,
        enrolledPackages,
      };

    companyBenefitPackages.forEach((benefit) => {
      const enrollments = employeeEnrollments?.filter(
        (item) => item.benefitPackageTag === benefit?.benefitPackageTag
      );

      enrollments.forEach((enrollment) => {
        const isDependent = enrollment?.isDependent;
        const approveEnrollStatusCode = enrollmentStatusOptions.find(
          (status) => status.value === enrollment?.enrollmentApprovalStatus
        )?.code;

        const cancelEnrollApprovalStatusCode = enrollmentStatusOptions.find(
          (status) => status.value === enrollment?.cancellationApprovalStatus
        )?.code;

        const hasEnrollApprovedStatus =
          approveEnrollStatusCode === EApprovalStatus.APPROVED || !approveEnrollStatusCode;

        if (enrollment.dateOfEnrollment && hasEnrollApprovedStatus) {
          enrolledPackages.push({
            ...benefit,
            ...enrollment,
            enrolled: true,
            cancellationPossibility: enrollment?.cancellationPossibility || "",
            employeeEnrollmentTag: enrollment.employeeEnrollmentTag,
            dateOfCancellation: enrollment.dateOfCancellation || "",
            dateOfEnrollment: enrollment.dateOfEnrollment || "",
            paidByCompany: enrollment.paidByCompany,
            paidByEmployee: enrollment.paidByEmployee,
            dateOfEligibility: enrollment.dateOfEligibility || "",
            endDateOfEligibility: enrollment.endDateOfEligibility || "",
            price: enrollment?.price,
            enrollmentDocuments: enrollment?.attachments || [],
            isUpgradeable: enrollment?.isUpgradeable,
            requiresEnrollmentApproval: enrollment?.requiresEnrollmentApproval,
            requiresCancellationApproval: enrollment?.requiresCancellationApproval,
            isDependent,
            rejectionComment: enrollment?.rejectionComment || "",
            cancelEnrollApprovalStatusCode,
            approveEnrollStatusCode,
            isGrECoSensitiveDataConsentGiven: enrollment?.isGrECoSensitiveDataConsentGiven,
            paymentType: enrollment?.paymentType,
            ...(isDependent
              ? { employeeDependentTag: enrollment?.employeeDependentTag }
              : { employeeLoginEmailAddress: userUniqueEmail }),
          });
        } else {
          eligiblePackages.push({
            ...benefit,
            ...enrollment,
            isDependent,
            employeeEnrollmentTag: enrollment.employeeEnrollmentTag,
            dateOfCancellation: enrollment.dateOfCancellation || "",
            dateOfEnrollment: enrollment.dateOfEnrollment || "",
            dateOfEligibility: enrollment.dateOfEligibility || "",
            endDateOfEligibility: enrollment.endDateOfEligibility || "",
            cancellationPossibility: enrollment?.cancellationPossibility || "",
            paidByCompany: enrollment.paidByCompany,
            paidByEmployee: enrollment.paidByEmployee,
            enrollmentDocuments: enrollment?.attachments || [],
            isUpgradeable: enrollment?.isUpgradeable,
            requiresEnrollmentApproval: enrollment?.requiresEnrollmentApproval,
            approveEnrollStatusCode,
            rejectionComment: enrollment?.rejectionComment || "",
            requiresCancellationApproval: enrollment?.requiresCancellationApproval,
            isGrECoSensitiveDataConsentGiven: enrollment?.isGrECoSensitiveDataConsentGiven,
            paymentType: enrollment?.paymentType,
            price: enrollment?.price,
            ...(isDependent
              ? { employeeDependentTag: enrollment?.employeeDependentTag }
              : { employeeLoginEmailAddress: userUniqueEmail }),
          });
        }
      });
    });

    return {
      eligiblePackages,
      enrolledPackages,
    };
  }, [employeeEnrollments, companyBenefitPackages, userUniqueEmail]);

  const isLoading = !companyBenefitPackages || !employeeEnrollments;

  const enrolledFilteredData = packageData.enrolledPackages?.filter((item) => {
    const expired = !isWithinInterval(new Date(), {
      start: new Date(item?.validFrom || ""),
      end: new Date(item?.validTo || ""),
    });
    if (!showExpired && expired && !showExpiredByTabSelection) return false;

    if (item.isDependent) {
      return item.employeeDependentTag === selectedPackageUser?.value;
    }
    return item.employeeLoginEmailAddress === selectedPackageUser?.value;
  });

  const enrolledPackagesImplementations = packageData?.enrolledPackages.reduce(
    (acc, item) => {
      const implementationKey = item.benefitImplementationTag || "";
      const employeeKey = item.isDependent
        ? item.employeeDependentTag
        : item.employeeLoginEmailAddress;
      const dateOfCancellation = item?.dateOfCancellation || "";

      if (dateOfCancellation) {
        // if (isBefore(new Date(dateOfCancellation), new Date())) {
        //   return acc;
        // }
        return acc;
      }

      if (item.isDependent) {
        acc.dependent[employeeKey || ""] = {
          ...acc.dependent[employeeKey || ""],
          [implementationKey]: item,
        };
      } else {
        acc.employee[employeeKey || ""] = {
          ...acc.employee[employeeKey || ""],
          [implementationKey]: item,
        };
      }

      return acc;
    },
    { employee: {}, dependent: {} } // Initialize with employee and dependent objects
  );

  const eligibleFilteredData = packageData.eligiblePackages?.filter((item) => {
    const packageExpired = !isWithinInterval(new Date(), {
      start: new Date(item?.validFrom || ""),
      end: new Date(item?.validTo || ""),
    });

    const today = startOfDay(new Date());
    const eligibilityExpired = isBefore(item.endDateOfEligibility || "", today);

    if (packageExpired || eligibilityExpired) return false;

    if (item.isDependent) {
      const enrollFromSameImplementation =
        enrolledPackagesImplementations?.dependent?.[item?.employeeDependentTag || ""]?.[
          item.benefitImplementationTag || ""
        ];

      if (enrollFromSameImplementation && !item.isUpgradeable) return false; // If the dependent is already enrolled in the same implementation, and the benefit is not upgradeable, return false

      return item.employeeDependentTag === selectedPackageUser?.value;
    }

    const enrollFromSameImplementation =
      enrolledPackagesImplementations?.employee?.[item?.employeeLoginEmailAddress || ""]?.[
        item.benefitImplementationTag || ""
      ];
    if (enrollFromSameImplementation && !item.isUpgradeable) return false; // If the employee is already enrolled in the same implementation, and the benefit is not upgradeable, return false

    return item.employeeLoginEmailAddress === selectedPackageUser?.value;
  });

  return {
    isLoading,
    packageData,
    enrolledFilteredData,
    eligibleFilteredData,
  };
}
